import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import {
  ChatbotAnswer,
  ChatbotHistoryItem,
  ChatbotMessage,
} from 'src/app/models/chatbot.model';
import { StaticChatbotService } from 'src/app/services/chatbot/static-chatbot.service';

interface ChatbotUiState {
  isSoundEnabled: boolean;
}

interface WebkitAudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

@Component({
  selector: 'app-chatbot-widget',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, MaterialModule],
  templateUrl: './chatbot-widget.component.html',
  styleUrl: './chatbot-widget.component.scss',
})
export class ChatbotWidgetComponent implements OnInit, OnDestroy {
  @ViewChild('messagePanel') private messagePanel?: ElementRef<HTMLElement>;
  @ViewChild('questionInput') private questionInput?: ElementRef<HTMLInputElement>;

  readonly stateStorageKey = 'flowdrizzle_chatbot_ui_state_v2';
  readonly suggestions = this.chatbotService.getSuggestions();

  isOpen = false;
  isTyping = false;
  isSoundEnabled = true;
  messages: ChatbotMessage[] = [];
  history: ChatbotHistoryItem[] = [];

  private responseTimer?: ReturnType<typeof setTimeout>;
  private audioContext?: AudioContext;

  questionControl = new FormControl('', {
  nonNullable: true,
  validators: [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(250),
  ],
});
  constructor(private readonly chatbotService: StaticChatbotService) {
    
  }

  ngOnInit(): void {
    this.restoreState();
    this.reloadLocalSession();
  }

  ngOnDestroy(): void {
    this.persistState();
    this.persistMessages();

    if (this.responseTimer) {
      clearTimeout(this.responseTimer);
    }
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.focusInput();
      this.scrollToBottom();
      this.playTone(520, 0.1);
    }
  }

  askSuggestion(question: string): void {
    if (this.isTyping) {
      return;
    }

    this.questionControl.setValue(question);
    this.askQuestion();
  }

  askFromHistory(item: ChatbotHistoryItem): void {
    this.askSuggestion(item.question);
  }

  askQuestion(): void {
    if (this.questionControl.invalid || this.isTyping) {
    this.questionControl.markAsTouched();
    return;
  }

  const question = this.questionControl.value.trim();

  if (!question) {
    this.questionControl.setErrors({ required: true });
    this.questionControl.markAsTouched();
    return;
  }

  this.questionControl.disable();
  this.isTyping = true;

    this.appendMessage({
      id: this.createMessageId(),
      sender: 'user',
      text: question,
      createdAt: new Date().toISOString(),
    });

    this.questionControl.reset('');
    this.isTyping = true;
    this.scrollToBottom();

    this.responseTimer = setTimeout(() => {
      const answer = this.chatbotService.ask(question);
      this.addBotAnswer(question, answer);
    }, 650);
  }

  refreshChat(): void {
    this.reloadLocalSession();
    this.scrollToBottom();
    this.focusInput();
    this.playTone(620, 0.08);
  }

  clearChat(): void {
    this.messages = [];
    this.history = [];
    this.isTyping = false;
    this.questionControl.reset('');
    this.chatbotService.clearMessages();
    this.chatbotService.clearHistory();
    this.playTone(420, 0.08);
    this.focusInput();
  }

  toggleSound(): void {
    this.isSoundEnabled = !this.isSoundEnabled;
    this.persistState();

    if (this.isSoundEnabled) {
      this.playTone(640, 0.08);
    }
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.askQuestion();
    }
  }

  trackByMessageId(_: number, message: ChatbotMessage): string | number {
    return message.id;
  }

  trackByHistoryId(_: number, item: ChatbotHistoryItem): string {
    return item.id;
  }

  isInternalLink(url: string): boolean {
    return url.startsWith('/');
  }

  get hasConversation(): boolean {
    return this.messages.length > 0;
  }

  get sessionLabel(): string {
    const count = this.history.length;
    return count === 1 ? '1 Q&A saved' : `${count} Q&A saved`;
  }

  private addBotAnswer(question: string, answer: ChatbotAnswer): void {
    this.appendMessage({
      id: this.createMessageId(),
      sender: 'bot',
      text: answer.answer,
      links: answer.links,
      createdAt: new Date().toISOString(),
    });

    this.history = [
      this.chatbotService.saveHistoryItem(question, answer),
      ...this.history.filter((item) => item.question.toLowerCase() !== question.toLowerCase()),
    ].slice(0, 30);

    this.isTyping = false;
    this.persistMessages();
    this.playTone(720, 0.1);
    this.scrollToBottom();
    this.focusInput();
  }

  private appendMessage(message: ChatbotMessage): void {
    this.messages = [...this.messages, message].slice(-80);
    this.persistMessages();
  }

  private reloadLocalSession(): void {
    this.messages = this.chatbotService.getMessages();
    this.history = this.chatbotService.getHistory();
  }

  private persistMessages(): void {
    this.chatbotService.saveMessages(this.messages);
  }

  private persistState(): void {
    const state: ChatbotUiState = {
      isSoundEnabled: this.isSoundEnabled,
    };

    localStorage.setItem(this.stateStorageKey, JSON.stringify(state));
  }

  private restoreState(): void {
    const savedState = localStorage.getItem(this.stateStorageKey);

    if (!savedState) {
      return;
    }

    try {
      const parsed = JSON.parse(savedState) as Partial<ChatbotUiState>;
      this.isSoundEnabled = parsed.isSoundEnabled ?? true;
    } catch {}
  }

  private focusInput(): void {
    setTimeout(() => this.questionInput?.nativeElement.focus(), 120);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const panel = this.messagePanel?.nativeElement;

      if (!panel) {
        return;
      }

      panel.scrollTo({
        top: panel.scrollHeight,
        behavior: 'smooth',
      });
    }, 80);
  }

  private createMessageId(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  }

  private playTone(frequency: number, duration: number): void {
    if (!this.isSoundEnabled) {
      return;
    }

    try {
      const audioWindow = window as WebkitAudioWindow;
      const AudioContextClass = window.AudioContext || audioWindow.webkitAudioContext;

      if (!AudioContextClass) {
        return;
      }

      this.audioContext ??= new AudioContextClass();

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.001, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.04, this.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      oscillator.start();
      oscillator.stop(this.audioContext.currentTime + duration);
    } catch {}
  }
}
